import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { getAdmin, adminApi } from "@/lib/api";
import {
  ShieldCheck, LayoutDashboard, Inbox as InboxIcon, Users, FileCheck2, Vote, Mail, ScrollText,
  Settings, LogOut, Menu, Newspaper, PanelsTopLeft, MailQuestion, DatabaseZap, Globe2, BellRing, Link2,
  HandHeart, Fingerprint, MailCheck, Megaphone, BrainCircuit, MapPinned, MessageSquareQuote, Flag,
  ClipboardCheck, Rocket, AlertTriangle, Sparkles,
} from "lucide-react";

const LINKS = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/applications", label: "Applications", icon: InboxIcon },
  { to: "/admin/candidates", label: "Candidates", icon: Users },
  { to: "/admin/candidate-data", label: "Candidate Data", icon: DatabaseZap },
  { to: "/admin/coverage", label: "Coverage", icon: Globe2 },
  { to: "/admin/coverage/spot-check", label: "Ward Spot-Check", icon: MapPinned },
  { to: "/admin/alerts", label: "Alerts", icon: BellRing },
  { to: "/admin/declarations", label: "Declarations", icon: FileCheck2 },
  { to: "/admin/witness", label: "Mutual Witness", icon: Link2 },
  { to: "/admin/witness-network", label: "Witness Network", icon: HandHeart },
  { to: "/admin/identity", label: "Identity", icon: Fingerprint },
  { to: "/admin/email-log", label: "Email Log", icon: MailCheck },
  { to: "/admin/email-mode", label: "Email Mode", icon: Mail },
  { to: "/admin/rehearsal", label: "Launch Rehearsal", icon: ClipboardCheck },
  { to: "/admin/launch-gate", label: "Launch Gate", icon: Rocket },
  { to: "/admin/beta", label: "Beta & Early Access", icon: Sparkles },
  { to: "/admin/polls", label: "Community Polls", icon: Megaphone },
  { to: "/admin/qa", label: "Candidate Q&A", icon: MessageSquareQuote },
  { to: "/admin/corrections", label: "Corrections", icon: Flag },
  { to: "/admin/intelligence", label: "Community Intelligence", icon: BrainCircuit },
  { to: "/admin/elections", label: "Elections", icon: Vote },
  { to: "/admin/website", label: "Website", icon: PanelsTopLeft },
  { to: "/admin/news", label: "News", icon: Newspaper },
  { to: "/admin/inbox", label: "Inbox", icon: MailQuestion },
  { to: "/admin/emails", label: "Email drafts", icon: Mail },
  { to: "/admin/audit-log", label: "Audit log", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const SidebarNav = () => (
  <nav className="flex flex-col gap-1" aria-label="Admin navigation">
    {LINKS.map((l) => (
      <NavLink
        key={l.to}
        to={l.to}
        data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium ${
            isActive ? "bg-primary text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
          }`
        }
      >
        <l.icon className="h-4 w-4" /> {l.label}
      </NavLink>
    ))}
  </nav>
);

export default function AdminLayout() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(getAdmin());
  const [watchNotices, setWatchNotices] = useState([]);
  const token = localStorage.getItem("wyv_admin_token");

  useEffect(() => {
    setAdmin(getAdmin());
  }, []);

  // Welland Watch banner — unread official-source detections. Detection NEVER auto-verifies:
  // the required workflow stays Review Source → Import → Spot-Check → Verify.
  useEffect(() => {
    if (!token) return;
    adminApi.get("/admin/coverage/watch")
      .then((r) => setWatchNotices((r.data.notices || []).filter((n) => !n.read)))
      .catch(() => {});
  }, [token]);

  if (!token) return <Navigate to="/admin" replace />;

  const logout = () => {
    localStorage.removeItem("wyv_admin_token");
    localStorage.removeItem("wyv_admin");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <aside className="hidden lg:flex w-60 bg-slate-900 flex-col fixed inset-y-0">
        <div className="flex items-center gap-2.5 px-4 h-16 border-b border-slate-800">
          <img src="/branding/line-dark.webp" alt="Witness Your Vote"
               className="h-7 w-auto" data-testid="admin-logo-img" />
          <p className="text-[10px] text-slate-400 uppercase tracking-widest">Admin</p>
        </div>
        <div className="p-3 flex-1 overflow-y-auto">
          <SidebarNav />
        </div>
        <div className="p-3 border-t border-slate-800">
          <p className="text-xs text-slate-400 px-2 truncate">{admin?.email}</p>
          <p className="text-[10px] text-slate-500 px-2 uppercase tracking-wider mb-2">Role: {admin?.role}</p>
          <Button variant="ghost" size="sm" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800" onClick={logout} data-testid="admin-logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 lg:ml-60">
        <div className="lg:hidden bg-slate-900 text-white flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            <span className="font-heading font-semibold text-sm">WYV Admin</span>
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white" aria-label="Open admin menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-slate-900 border-slate-800 w-64 overflow-y-auto">
              <SheetTitle className="text-white font-heading text-left mb-4">Admin menu</SheetTitle>
              <SidebarNav />
              <Button variant="ghost" size="sm" className="w-full justify-start text-slate-300 mt-4" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </Button>
            </SheetContent>
          </Sheet>
        </div>
        <main className="p-4 sm:p-6 lg:p-8">
          {watchNotices.length > 0 && (
            <div className="mb-6 rounded-xl border-2 border-amber-400 bg-amber-50 p-4"
                 role="alert" data-testid="welland-watch-banner">
              <div className="flex flex-wrap items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1 basis-60">
                  <p className="font-heading font-bold text-sm text-amber-900 tracking-wide"
                     data-testid="welland-watch-banner-title">
                    WELLAND BOUNDARY UPDATE DETECTED
                  </p>
                  <p className="text-xs text-amber-900/90 mt-0.5 break-words">
                    A possible official 2026 ward-boundary source has been found.
                    {watchNotices.length > 1 ? ` ${watchNotices.length} unreviewed detections.` : ""}
                  </p>
                  <p className="text-[11px] text-amber-800 mt-1 break-words"
                     data-testid="welland-watch-banner-detail">
                    Detected: {new Date(watchNotices[0].created_at).toLocaleString()}
                    {" — Source: "}
                    {watchNotices[0].detection?.title || watchNotices[0].title}
                    {watchNotices[0].detection?.source ? ` (${watchNotices[0].detection.source})` : ""}
                  </p>
                  <p className="text-[10px] text-amber-700 mt-1 italic">
                    Required workflow: Review Source → Import → Spot-Check → Verify. Detection never auto-verifies.
                  </p>
                </div>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                        onClick={() => navigate("/admin/coverage")}
                        data-testid="welland-watch-banner-review">
                  Review source
                </Button>
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
